import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, avatarUrl, guildIconUrl, timeAgo } from '../api.js';

export default function Owner({ me, setMe }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { reload(); }, []);

  function reload() {
    setData(null);
    api.ownerGuilds().then(setData).catch(err => setData({ error: err.message }));
  }

  async function runAction(fn, success) {
    try { await fn(); setToast({ type: 'success', msg: success }); reload(); }
    catch (err) { setToast({ type: 'error', msg: err.message }); }
    finally { setModal(null); setTimeout(() => setToast(null), 3000); }
  }

  async function handleLogout() {
    await api.logout();
    setMe({ loading: false, user: null });
  }

  if (!data) return <div className="empty">Loading…</div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.5rem' }} onClick={() => navigate('/')}>
            <img className="brand-icon" src="/soupos-icon.png" alt="" /> SoupOS
          </span>
          <span className="dim">/</span>
          <span>Owner panel</span>
        </div>
        <div className="row" style={{ gap: '.75rem', flex: 0 }}>
          <img className="avatar" src={avatarUrl(me.user)} alt="" />
          <span>{me.user.global_name || me.user.username}</span>
          <button className="ghost" onClick={handleLogout}>Log out</button>
        </div>
      </div>

      <div className="content">
        <h1>All servers ({data.guilds.length})</h1>
        <p className="subtitle">Every server SoupOS is currently installed in. Leave or ban any of them from re-joining.</p>

        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr><th></th><th>Server</th><th>Members</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {data.guilds.map(g => (
                <tr key={g.id}>
                  <td style={{ width: 48 }}>
                    {guildIconUrl(g)
                      ? <img className="avatar" src={guildIconUrl(g)} alt="" />
                      : <div className="avatar" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{g.name.slice(0,1).toUpperCase()}</div>}
                  </td>
                  <td>
                    <Link to={`/g/${g.id}/config`}>{g.name}</Link>
                    <div className="dim" style={{ fontSize: 12 }}><span className="tag">{g.id}</span>{g.owner && <span className="badge" style={{ marginLeft: 6 }}>you own</span>}</div>
                  </td>
                  <td>{g.member_count ?? <span className="dim">—</span>}</td>
                  <td>
                    {g.blacklisted
                      ? <span className="badge danger">blacklisted</span>
                      : <span className="badge success">active</span>}
                  </td>
                  <td>
                    <div className="row" style={{ gap: '.4rem', flex: 0, justifyContent: 'flex-end' }}>
                      <button className="ghost" onClick={() => setModal({ type: 'leave', guild: g })}>Leave</button>
                      <button className="danger" onClick={() => setModal({ type: 'ban', guild: g })}>Ban</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 style={{ marginTop: '2.5rem' }}>Blacklisted servers not currently in</h3>
        <p className="muted" style={{ marginTop: '-.5rem' }}>The bot is blocked from joining these. Remove from the blacklist if you want to allow them back.</p>
        {!data.blacklist.length ? (
          <div className="empty">No standing blacklist entries.</div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <table className="table">
              <thead><tr><th>Guild ID</th><th>Reason</th><th>When</th><th></th></tr></thead>
              <tbody>
                {data.blacklist.map(b => (
                  <tr key={b.guild_id}>
                    <td className="tag">{b.guild_id}</td>
                    <td>{b.reason || <span className="dim">—</span>}</td>
                    <td>{timeAgo(b.created_at)}</td>
                    <td>
                      <button className="ghost" onClick={() => runAction(() => api.ownerUnblacklist(b.guild_id), 'Removed from blacklist')}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {modal && <LeaveModal
          guild={modal.guild}
          ban={modal.type === 'ban'}
          onCancel={() => setModal(null)}
          onSubmit={(body) => runAction(
            () => api.ownerLeave(modal.guild.id, body),
            modal.type === 'ban' ? `Left + blacklisted ${modal.guild.name}` : `Left ${modal.guild.name}`)}
        />}

        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </div>
    </div>
  );
}

function LeaveModal({ guild, ban, onCancel, onSubmit }) {
  const [reason, setReason] = useState('');
  const [alsoBlacklist, setAlsoBlacklist] = useState(ban);

  function submit(e) {
    e.preventDefault();
    onSubmit({ blacklist: alsoBlacklist, reason });
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{ban ? 'Ban & leave' : 'Leave'} {guild.name}</h3>
        <form onSubmit={submit}>
          {ban && <p className="muted" style={{ marginTop: 0 }}>
            The bot will leave and refuse to be re-invited to this server until you remove the blacklist entry.
          </p>}
          <div className="field">
            <label>Reason (for your records)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} autoFocus />
          </div>
          {!ban && (
            <div className="field">
              <label style={{ display: 'flex', gap: '.5rem', alignItems: 'center', textTransform: 'none', letterSpacing: 'normal' }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={alsoBlacklist} onChange={e => setAlsoBlacklist(e.target.checked)} />
                Also blacklist (prevent re-joining)
              </label>
            </div>
          )}
          <div className="row actions" style={{ gap: '.5rem' }}>
            <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className={ban || alsoBlacklist ? 'danger' : 'warn'}>
              {ban || alsoBlacklist ? 'Leave & blacklist' : 'Leave'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
